import { MappedEntity } from '@toggle/toggle';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useEntities } from '~/hooks/use-entities';
import { useWatchlist } from '~/stores/use-watchlist/useWatchlist';

import { WatchlistMessageTable } from '../watchlist-message-table/WatchlistMessageTable';
import * as S from './WatchlistEntityMatchesTable.styles';

export interface WatchlistEntityMatchesTableProps {
  tags: string[];
  watchlistId: string;
  watchlistName?: string;
  name: string;
  onAddToWatchlist?: (entity: MappedEntity) => void;
}

export const WatchlistEntityMatchesTable = ({
  tags,
  watchlistId,
  watchlistName,
  name,
  onAddToWatchlist,
}: WatchlistEntityMatchesTableProps) => {
  const { t } = useTranslation(['common', 'watchlistManager']);
  const {
    addItemToList,
    createList,
    removeItemFromList,
    getList,
    listIdsByTag,
  } = useWatchlist(state => ({
    addItemToList: state.addItemToList,
    createList: state.createList,
    removeItemFromList: state.removeItemFromList,
    getList: state.getList,
    listIdsByTag: state.listIdsByTag,
  }));
  const watchlistIdRef = useRef<string | null>(watchlistId);

  const { data } = useEntities({ entities: tags });
  const entities = data || [];

  // Refresh store in case Alfred removed an asset
  useEffect(() => {
    if (watchlistId) {
      getList(watchlistId);
    }
  }, [watchlistId]);

  const addToWatchList = async (entity: MappedEntity) => {
    if (watchlistIdRef.current) {
      await addItemToList(watchlistIdRef.current, entity.tag);
    } else if (watchlistName) {
      watchlistIdRef.current = await createList(watchlistName, [entity.tag]);
    }

    onAddToWatchlist?.(entity);
  };

  const removeFromWatchList = (entity: MappedEntity) => {
    if (watchlistIdRef.current) {
      removeItemFromList(watchlistIdRef.current, entity.tag);
    }
  };

  return (
    <WatchlistMessageTable
      entities={entities}
      name={name}
      renderActionColumn={entity => {
        const inWatchlist = !!listIdsByTag
          .get(entity.tag)
          ?.some((id: string) => id === watchlistIdRef.current);

        return (
          <S.StyledButton
            $inWatchlist={inWatchlist}
            data-testid={
              inWatchlist ? 'remove-from-watchlist' : 'add-to-watchlist'
            }
            iconName={inWatchlist ? 'Checkmark' : 'Add'}
            label={
              inWatchlist
                ? t('watchlistManager:watching')
                : t('common:addToWatchlist')
            }
            variant="secondary"
            onClick={
              inWatchlist
                ? () => removeFromWatchList(entity)
                : () => addToWatchList(entity)
            }
          />
        );
      }}
    />
  );
};
